import { Card, Checkbox, Modal, Popconfirm } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import tw from "tailwind-styled-components";
import {
	clearExpenseForm,
	loadSingleExpenseForm,
	updateExpenseForm,
} from "../../redux/rtk/features/expense/expenseSlice";
import getUserFromToken from "../../utils/getUserFromToken";
import Loader from "../loader/loader";
import PageTitle from "../page-header/PageHeader";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import ReviewExpensePopup from "../UI/PopUp/ReviewExpensePopup";

const DetailExpense = () => {
	const { id } = useParams("id");
	const expense = useSelector((state) => state?.expense?.expense);
	const dispatch = useDispatch();
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewData, setPreviewData] = useState(null);
	const [previewType, setPreviewType] = useState(null);

	useEffect(() => {
		dispatch(loadSingleExpenseForm(id));

		return () => {
			dispatch(clearExpenseForm());
		};
	}, []);

	const handlePaymentStatusChange = (checked) => {
		const newStatus = checked ? "PAID" : "UNPAID";

		dispatch(
			updateExpenseForm({
				id,
				values: { paymentStatus: newStatus },
			})
		)
			.unwrap()
			.then(() => {
				dispatch(loadSingleExpenseForm(id)); // Refresh the expense data
				// toast.success(`Payment status updated to ${newStatus}`);
			})
			.catch((error) => {
				console.error("Error updating payment status:", error);
				// toast.error("Failed to update payment status");
			});
	};

	const getFileExtension = (url) => {
		if (!url) return null;
		const extension = url.split('.').pop().toLowerCase();
		return extension;
	};

	const getFileType = (extension) => {
		if (!extension) return null;

		if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
			return 'image';
		} else if (extension === 'pdf') {
			return 'pdf';
		} else if (['xls', 'xlsx', 'csv'].includes(extension)) {
			return 'excel';
		} else if (['doc', 'docx'].includes(extension)) {
			return 'doc';
		}

		return 'unknown';
	};

	const handlePreview = (e) => {
		e.preventDefault();
		const token = getUserFromToken();
		if (!token) {
			console.error("Authorization token missing");
			return;
		}

		fetch(expense.expenseProofUrl, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${localStorage.getItem("access-token")}`,
			},
		})
			.then((response) => {
				if (response.ok) {
					return response.blob();
				} else {
					throw new Error("Authorization failed");
				}
			})
			.then((blob) => {
				const extension = getFileExtension(expense.expenseProofUrl);
				const fileType = getFileType(extension);
				setPreviewType(fileType);

				const url = window.URL.createObjectURL(blob);
				setPreviewData(url);
				setPreviewVisible(true);
			})
			.catch((error) => {
				console.error("Error fetching attachment: ", error);
			});
	};

	const handleDownload = () => {
		if (!previewData) return;

		const a = document.createElement("a");
		a.href = previewData;
		a.download = expense.attachment || "attachment";
		document.body.appendChild(a);
		a.click();
		a.remove();
	};

	const renderPreview = () => {
		if (!previewData) return null;

		switch (previewType) {
			case 'image':
				return (
					<div className="flex flex-col items-center">
						<img
							src={previewData}
							alt="Expense attachment"
							className="max-w-full max-h-96 mb-4"
						/>
					</div>
				);
			case 'pdf':
				return (
					<div className="flex flex-col items-center">
						<iframe
							src={previewData}
							title="PDF Preview"
							className="w-full h-96 mb-4"
						/>
					</div>
				);
			case 'excel':
			case 'doc':
				return (
					<div className="text-center p-6 bg-gray-100 rounded-lg">
						<p className="text-lg mb-4">
							Preview not available for {previewType === 'excel' ? 'Excel' : 'Word'} files.
						</p>
						<p className="mb-4">
							Filename: {expense.attachment || "attachment"}
						</p>
					</div>
				);
			default:
				return (
					<div className="text-center p-6 bg-gray-100 rounded-lg">
						<p className="text-lg mb-4">
							Preview not available for this file type.
						</p>
						<p className="mb-4">
							Filename: {expense.attachment || "attachment"}
						</p>
					</div>
				);
		}
	};

	return (
		<div>
			<PageTitle title='Back' />
			<UserPrivateComponent permission={"readSingle-leaveApplication"}>
				<Card className='mt-4'>
					<div className='text-center mb-4'>
						{" "}
						<h2 className='text-2xl font-semibold text-gray-600'>
							Expense Sheet #{expense?.id}{" "}
						</h2>
					</div>
					{expense ? (
						<div className='flex justify-center '>
							<ul className='list-inside list-none border-2 border-inherit rounded px-5 py-5 '>
								<ListItem>
									Name :{" "}
									<TextInside>
										{(
											expense?.user.firstName +
											" " +
											expense?.user.lastName
										).toUpperCase()}{" "}
									</TextInside>
								</ListItem>
								<ListItem>
									Expense Title : <TextInside>{expense.expenseTitle}</TextInside>
								</ListItem>
								<ListItem>
									Expense Amount :<TextInside>£ {expense.expenseAmount}</TextInside>
								</ListItem>
								<ListItem>
									Expense Date :{" "}
									<TextInside>
										{dayjs(expense.expenseDate).format("DD-MM-YYYY")}
									</TextInside>
								</ListItem>

								<ListItem>
									Expense Status :{" "}
									<TextInside>
										{expense.approvalStatus === "PENDING" ? (
											<span className='text-yellow-500'>
												{expense.approvalStatus.toUpperCase()}
											</span>
										) : expense.approvalStatus === "APPROVED" ? (
											<span className='text-green-500'>
												{expense.approvalStatus.toUpperCase()}
											</span>
										) : expense.approvalStatus === "PARTIAL APPROVED" ? (
											<span className='text-orange-500'>
												{expense.approvalStatus.toUpperCase()}
											</span>
										) : (
											<span className='text-red-500'>
												{expense.approvalStatus.toUpperCase()}
											</span>
										)}
									</TextInside>
								</ListItem>
								<ListItem>
									Approved Amount :<TextInside>£ {expense.approvedAmount}</TextInside>
								</ListItem>

								<ListItem>
									Approval Date :{" "}
									<TextInside>
										{expense.approvedDate
											? dayjs(expense.approvedDate).format("DD-MM-YYYY")
											: "ON REVIEW"}
									</TextInside>
								</ListItem>

								<ListItem>
									Expense Approved By :{" "}
									<TextInside className='text-green-500'>
										{(expense.approveExpenseBy?.firstName || "ON") +
											" " +
											(expense.approveExpenseBy?.lastName || "REVIEW")}
									</TextInside>
								</ListItem>

								<ListItem>
									Review Comment :{" "}
									<TextInside>{expense.reviewComment || "No comment"}</TextInside>
								</ListItem>

								{/* <ListItem>
									Payment Status :{" "}
									<TextInside>
										{expense.paymentStatus === "Unpaid" ? (
											<span className='text-red-500'>
												{expense.paymentStatus.toUpperCase()}
											</span>
										) : expense.paymentStatus === "PAID" ? (
											<span className='text-green-500'>
												{expense.paymentStatus.toUpperCase()}
											</span>
										) : (
											<span className='text-yellow-500'>
												{expense.paymentStatus.toUpperCase()}
											</span>
										)}
									</TextInside>
								</ListItem> */}
								<ListItem>
									Payment Status:{" "}
									<TextInside>
										<UserPrivateComponent permission={"readAll-role"}>
											<Popconfirm
												title={`Are you sure you want to mark this expense as ${expense.paymentStatus === "PAID" ? "UNPAID" : "PAID"}?`}
												onConfirm={() => handlePaymentStatusChange(expense.paymentStatus !== "PAID")}
												okText="Yes"
												cancelText="No"
												// disabled={expense.paymentStatus === "PAID"}
											>
												<Checkbox
													checked={expense.paymentStatus === "PAID"}
													// disabled={expense.paymentStatus === "PAID"}
												>
													{expense.paymentStatus === "PAID" ? (
														<span className="text-green-500">PAID</span>
													) : (
														<span className="text-red-500">UNPAID</span>
													)}
												</Checkbox>
											</Popconfirm>
										</UserPrivateComponent>

										<UserPrivateComponent permission={"readAll-role"} denyContent={
											expense.paymentStatus === "PAID" ? (
												<span className="text-green-500">PAID</span>
											) : (
												<span className="text-red-500">UNPAID</span>
											)
										}>
											{/* This is an empty block - the denyContent will be shown if user doesn't have permission */}
										</UserPrivateComponent>
									</TextInside>
								</ListItem>

								<ListItem>
									Attachment:{" "}
									<TextInside>
										{expense.attachment ? (
											<span className="flex gap-2">
												<button
													onClick={handlePreview}
													className="text-blue-500 hover:text-blue-700 mr-2"
												>
													Preview
												</button>
											</span>
										) : (
											"No Attachment"
										)}
									</TextInside>
								</ListItem>

							</ul>
						</div>
					) : (
						<Loader />
					)}
					<UserPrivateComponent permission={"update-leaveApplication"}>
						{expense?.approvalStatus === "PENDING" && (
							<div className='flex justify-center items-center'>
								<ReviewExpensePopup />
							</div>
						)}
						{expense?.approvalStatus === "REJECTED" && (
							<div className='flex justify-center items-center'>
								<ReviewExpensePopup />
							</div>
						)}
					</UserPrivateComponent>
				</Card>
			</UserPrivateComponent>

			<Modal
				title="Attachment Preview"
				open={previewVisible}
				onCancel={() => setPreviewVisible(false)}
				width={800}
				footer={[
					<button
						key="download"
						onClick={handleDownload}
						className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
					>
						Download
					</button>,
					<button
						key="close"
						onClick={() => setPreviewVisible(false)}
						className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded ml-2"
					>
						Close
					</button>
				]}
			>
				{renderPreview()}
			</Modal>
		</div>
	);
};

const ListItem = tw.li`
text-sm
text-gray-600
font-semibold
py-2
px-4
bg-gray-100
mb-1.5
rounded
w-96
flex
justify-start
`;

const TextInside = tw.p`
ml-2
text-sm
text-gray-900
`;
export default DetailExpense;



// import { Card } from "antd";
// import dayjs from "dayjs";
// import { useEffect } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { useParams } from "react-router-dom";
// import tw from "tailwind-styled-components";
// import {
// 	clearExpenseForm,
// 	loadSingleExpenseForm,
// } from "../../redux/rtk/features/expense/expenseSlice";
// import getUserFromToken from "../../utils/getUserFromToken";
// import Loader from "../loader/loader";
// import PageTitle from "../page-header/PageHeader";
// import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
// import ReviewExpensePopup from "../UI/PopUp/ReviewExpensePopup";

// const DetailExpense = () => {
// 	const { id } = useParams("id");
// 	const expense = useSelector((state) => state.expense.expense);
// 	const dispatch = useDispatch();

// 	useEffect(() => {
// 		dispatch(loadSingleExpenseForm(id));

// 		return () => {
// 			dispatch(clearExpenseForm());
// 		};
// 	}, []);
// 	console.log("Expense Attachment: ", expense?.attachment);


// 	const handleDownload = (e) => {
// 		e.preventDefault();
// 		const token = getUserFromToken();
// 		if (!token) {
// 			console.error("Authorization token missing");
// 			return;
// 		}

// 		fetch(expense.expenseProofUrl, {
// 			method: "GET",
// 			headers: {
// 				Authorization: `Bearer ${localStorage.getItem("access-token")}`,
// 			},
// 		})
// 			.then((response) => {
// 				if (response.ok) {
// 					return response.blob();
// 				} else {
// 					throw new Error("Authorization failed");
// 				}
// 			})
// 			.then((blob) => {
// 				const url = window.URL.createObjectURL(blob);
// 				const a = document.createElement("a");
// 				a.href = url;
// 				a.download = "attachment";
// 				document.body.appendChild(a);
// 				a.click();
// 				a.remove();
// 			})
// 			.catch((error) => {
// 				console.error("Error downloading attachment: ", error);
// 			});
// 	};

// 	return (
// 		<div>
// 			<PageTitle title='Back' />
// 			<UserPrivateComponent permission={"readSingle-leaveApplication"}>
// 				<Card className='mt-4'>
// 					<div className='text-center mb-4'>
// 						{" "}
// 						<h2 className='text-2xl font-semibold text-gray-600'>
// 							Expense Sheet #{expense?.id}{" "}
// 						</h2>
// 					</div>
// 					{expense ? (
// 						<div className='flex justify-center '>
// 							<ul className='list-inside list-none border-2 border-inherit rounded px-5 py-5 '>
// 								<ListItem>
// 									Name :{" "}
// 									<TextInside>
// 										{(
// 											expense?.user.firstName +
// 											" " +
// 											expense?.user.lastName
// 										).toUpperCase()}{" "}
// 									</TextInside>
// 								</ListItem>
// 								<ListItem>
// 									Expense Title : <TextInside>{expense.expenseTitle}</TextInside>
// 								</ListItem>
// 								<ListItem>
// 									Expense Amount :<TextInside>£ {expense.expenseAmount}</TextInside>
// 								</ListItem>
// 								<ListItem>
// 									Expense Date :{" "}
// 									<TextInside>
// 										{dayjs(expense.expenseDate).format("DD-MM-YYYY")}
// 									</TextInside>
// 								</ListItem>

// 								{/* <ListItem>
// 									Expense Duration :{" "}
// 									<TextInside className='text-red-500'>
// 										{expense.expenseDuration}
// 									</TextInside>
// 								</ListItem>

// 								<ListItem>
// 									Expense Reason :{" "}
// 									<TextInside>{expense.reason || "No reason"}</TextInside>
// 								</ListItem> */}

// 								<ListItem>
// 									Expense Status :{" "}
// 									<TextInside>
// 										{expense.approvalStatus === "PENDING" ? (
// 											<span className='text-yellow-500'>
// 												{expense.approvalStatus.toUpperCase()}
// 											</span>
// 										) : expense.approvalStatus === "APPROVED" ? (
// 											<span className='text-green-500'>
// 												{expense.approvalStatus.toUpperCase()}
// 											</span>
// 										) : expense.approvalStatus === "PARTIAL APPROVED" ? (
// 											<span className='text-orange-500'>
// 												{expense.approvalStatus.toUpperCase()}
// 											</span>
// 										) : (
// 											<span className='text-red-500'>
// 												{expense.approvalStatus.toUpperCase()}
// 											</span>
// 										)}
// 									</TextInside>
// 								</ListItem>

// 								{/* <ListItem>
// 									Expense Accepted From :{" "}
// 									<TextInside>
// 										{expense.acceptExpenseFrom
// 											? dayjs(expense.acceptExpenseFrom).format("DD-MM-YYYY")
// 											: "ON REVIEW"}
// 									</TextInside>
// 								</ListItem> */}
// 								<ListItem>
// 									Approved Amount :<TextInside>£ {expense.approvedAmount}</TextInside>
// 								</ListItem>

// 								<ListItem>
// 									Approval Date :{" "}
// 									<TextInside>
// 										{expense.approvedDate
// 											? dayjs(expense.approvedDate).format("DD-MM-YYYY")
// 											: "ON REVIEW"}
// 									</TextInside>
// 								</ListItem>

// 								<ListItem>
// 									Expense Approved By :{" "}
// 									<TextInside className='text-green-500'>
// 										{(expense.approveExpenseBy?.firstName || "ON") +
// 											" " +
// 											(expense.approveExpenseBy?.lastName || "REVIEW")}
// 									</TextInside>
// 								</ListItem>

// 								<ListItem>
// 									Review Comment :{" "}
// 									<TextInside>{expense.reviewComment || "No comment"}</TextInside>
// 								</ListItem>

// 								<ListItem>
// 									Payment Status :{" "}
// 									<TextInside>
// 										{expense.paymentStatus === "Unpaid" ? (
// 											<span className='text-red-500'>
// 												{expense.paymentStatus.toUpperCase()}
// 											</span>
// 										) : expense.paymentStatus === "PAID" ? (
// 											<span className='text-green-500'>
// 												{expense.paymentStatus.toUpperCase()}
// 											</span>
// 										) : (
// 											<span className='text-yellow-500'>
// 												{expense.paymentStatus.toUpperCase()}
// 											</span>
// 										)}
// 									</TextInside>
// 								</ListItem>

// 								<ListItem>
// 									Attachment:{" "}
// 									<TextInside>
// 										{expense.attachment ? (
// 											<a
// 												href={expense.expenseProofUrl}
// 												onClick={handleDownload} // Attach the download handler
// 												className="text-blue-500"
// 											>
// 												Download
// 											</a>
// 										) : (
// 											"No Attachment"
// 										)}
// 									</TextInside>
// 								</ListItem>

// 							</ul>
// 						</div>
// 					) : (
// 						<Loader />
// 					)}
// 					<UserPrivateComponent permission={"update-leaveApplication"}>
// 						{expense?.approvalStatus === "PENDING" && (
// 							<div className='flex justify-center items-center'>
// 								<ReviewExpensePopup />
// 							</div>
// 						)}
// 						{expense?.approvalStatus === "REJECTED" && (
// 							<div className='flex justify-center items-center'>
// 								<ReviewExpensePopup />
// 							</div>
// 						)}
// 					</UserPrivateComponent>
// 				</Card>
// 			</UserPrivateComponent>
// 		</div>
// 	);

// 	// "reviewComment": null,
// 	// "attachment": null,
// };

// const ListItem = tw.li`
// text-sm
// text-gray-600
// font-semibold
// py-2
// px-4
// bg-gray-100
// mb-1.5
// rounded
// w-96
// flex
// justify-start
// `;

// const TextInside = tw.p`
// ml-2
// text-sm
// text-gray-900
// `;
// export default DetailExpense;
